import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleContent } from '@/mocks/article-content';
import { articles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://superbuy.baby';

const relatedArticleImages: Record<string, string> = {
  'superbuy-safe-shopping-tips': 'https://readdy.ai/api/search-image?query=Secure%20padlock%20on%20warm%20stone%20surface%20soft%20natural%20light%20security%20concept%20minimalist%20photography%20neutral%20cream%20background&width=400&height=250&seq=art-rel-safe-v3-sb&orientation=landscape',
  'superbuy-qc-inspection-guide': 'https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20fabric%20on%20warm%20linen%20quality%20inspection%20minimalist%20product%20photography%20neutral%20beige&width=400&height=250&seq=art-rel-qc-v3-sb&orientation=landscape',
  'superbuy-shipping-cost-guide': 'https://readdy.ai/api/search-image?query=Cardboard%20packages%20on%20warm%20wood%20table%20international%20shipping%20concept%20soft%20morning%20light%20minimalist%20logistics%20neutral%20cream&width=400&height=250&seq=art-rel-ship-v3-sb&orientation=landscape',
  'superbuy-budget-spreadsheet-guide': 'https://readdy.ai/api/search-image?query=Coins%20on%20warm%20oak%20surface%20soft%20golden%20light%20budget%20concept%20minimalist%20finance%20photography%20neutral%20cream&width=400&height=250&seq=art-rel-budget-v3-sb&orientation=landscape',
  'superbuy-top-finds-june-2026': 'https://readdy.ai/api/search-image?query=Trendy%20fashion%20items%20on%20warm%20marble%20curated%20collection%20soft%20lighting%20minimalist%20editorial%20neutral%20earth%20tones&width=400&height=250&seq=art-rel-finds-v3-sb&orientation=landscape',
  'superbuy-haul-review-guide': 'https://readdy.ai/api/search-image?query=Open%20shopping%20bag%20on%20warm%20floor%20unboxing%20moment%20natural%20daylight%20lifestyle%20photography%20neutral%20beige%20interior&width=400&height=250&seq=art-rel-haul-v3-sb&orientation=landscape',
  'superbuy-best-sneaker-spreadsheet': 'https://readdy.ai/api/search-image?query=Designer%20sneakers%20on%20minimal%20shelf%20sneakerhead%20collection%20soft%20studio%20lighting%20warm%20cream%20background%20clean%20modern&width=400&height=250&seq=art-rel-sneak-v3-sb&orientation=landscape',
  'superbuy-streetwear-spreadsheet': 'https://readdy.ai/api/search-image?query=Streetwear%20hoodie%20flatlay%20on%20warm%20concrete%20urban%20fashion%20soft%20natural%20light%20minimalist%20neutral%20grey%20tones&width=400&height=250&seq=art-rel-street-v3-sb&orientation=landscape',
  'superbuy-size-conversion-guide': 'https://readdy.ai/api/search-image?query=Measuring%20tape%20on%20warm%20linen%20sizing%20guide%20concept%20soft%20morning%20light%20minimalist%20neutral%20cream&width=400&height=250&seq=art-rel-size-v3-sb&orientation=landscape',
  'superbuy-seller-verification-guide': 'https://readdy.ai/api/search-image?query=Five%20stars%20on%20warm%20minimal%20surface%20seller%20rating%20concept%20soft%20shadows%20clean%20photography%20neutral%20beige&width=400&height=250&seq=art-rel-seller-v3-sb&orientation=landscape',
  'superbuy-custom-order-guide': 'https://readdy.ai/api/search-image?query=Fabric%20swatches%20on%20warm%20wood%20desk%20custom%20design%20concept%20natural%20light%20creative%20studio%20neutral%20cream&width=400&height=250&seq=art-rel-custom-v3-sb&orientation=landscape',
  'superbuy-agent-comparison-guide': 'https://readdy.ai/api/search-image?query=Smartphone%20on%20warm%20minimal%20desk%20shopping%20app%20concept%20soft%20natural%20light%20lifestyle%20tech%20neutral%20cream&width=400&height=250&seq=art-rel-agent-v3-sb&orientation=landscape',
  'superbuy-spreadsheet-guide': 'https://readdy.ai/api/search-image?query=Organized%20minimalist%20desk%20with%20laptop%20and%20fashion%20lookbook%20warm%20sunlight%20editorial%20workspace%20cream%20and%20beige&width=400&height=250&seq=art-rel-guide-v3-sb&orientation=landscape',
};

function getRelatedImage(slug: string): string {
  const key = slug.replace('/', '');
  return relatedArticleImages[key] || relatedArticleImages['superbuy-spreadsheet-guide'];
}

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname;
  const content = useMemo(() => getArticleContent(slug), [slug]);

  const [activeToc, setActiveToc] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (!content) return;
    const handleScroll = () => {
      const scrollY = window.scrollY + 140;
      let current = '';
      for (const item of content.tocItems) {
        const el = document.getElementById(item.id);
        if (el && el.offsetTop <= scrollY) {
          current = item.id;
        }
      }
      if (current) setActiveToc(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [content]);

  if (!content) {
    return (
      <>
        <header><Navbar /></header>
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer">
              <i className="ri-home-4-line"></i> Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: content.title,
    author: { '@type': 'Person', name: 'Superbuy Research Team', jobTitle: 'Shopping Research Specialist' },
    datePublished: content.publishDate,
    dateModified: content.updatedDate,
    description: content.excerpt,
    publisher: { '@type': 'Organization', name: 'Superbuy Baby' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: content.breadcrumbs.map((bc, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: bc.name,
      item: bc.href ? `${SITE_URL}${bc.href}` : `${SITE_URL}${slug}`,
    })),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const authorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Superbuy Research Team',
    jobTitle: 'Shopping Research Specialist',
    description: '5+ years in international shopping guides. Specialties include Superbuy product sourcing, spreadsheet research, shipping optimization, and quality control verification.',
  };

  const related = articles.filter((a) => content.relatedSlugs.includes(a.slug.replace('/', ''))).slice(0, 4);

  return (
    <>
      <header><Navbar /></header>

      <main className="pt-20">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-10 py-4">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
            {content.breadcrumbs.map((bc, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <i className="ri-arrow-right-s-line text-[10px]"></i>}
                {bc.href ? (
                  <Link to={bc.href} className="hover:text-primary-500 transition-colors">{bc.name}</Link>
                ) : (
                  <span className="text-foreground-600 font-medium">{bc.name}</span>
                )}
              </span>
            ))}
          </nav>
        </div>

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-10">
          <div className="mb-4">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">{content.category}</span>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-6">
            {content.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-background-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-200 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20warm%20neutral%20tones%20clean%20studio%20lighting%20friendly%20portrait%20modern%20editorial&width=120&height=120&seq=article-author-dyn-v3-sb&orientation=squarish"
                  alt="Superbuy Research Team author"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800">Superbuy Research Team</p>
                <p className="text-xs text-foreground-400">Shopping Research Specialist &bull; 5+ years experience</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-foreground-400 ml-auto">
              <span className="flex items-center gap-1"><i className="ri-time-line"></i> {content.readingTime} min read</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> Published: {content.publishDate}</span>
              <span className="flex items-center gap-1 text-accent-600 font-medium"><i className="ri-refresh-line"></i> Updated: {content.updatedDate}</span>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden mb-10 aspect-[16/9] w-full">
            <img
              src={content.featuredImageUrl}
              alt={content.title}
              title={content.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-[280px] shrink-0">
              <div className="lg:sticky lg:top-24 bg-background-100 rounded-2xl p-5">
                <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-4">Contents</h4>
                <nav className="space-y-1">
                  {content.tocItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-colors cursor-pointer ${
                        activeToc === item.id
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                      }`}
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {content.sections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className={`mb-12 ${section.isHighlight ? 'bg-accent-50 rounded-2xl p-6 md:p-8' : ''}`}
                >
                  <h2 className={`font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4 ${section.highlightIcon ? 'flex items-center gap-2' : ''}`}>
                    {section.highlightIcon && <i className={`${section.highlightIcon} text-accent-600`}></i>}
                    {section.title}
                  </h2>
                  <div
                    className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed article-content"
                    dangerouslySetInnerHTML={{ __html: section.contentHtml }}
                  />
                </section>
              ))}

              {content.comparisonTable && (
                <section id="comparison" className="mb-12">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Comparison: Superbuy Spreadsheet vs Manual Browsing</h2>
                  <div className="overflow-x-auto rounded-xl border border-background-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="text-left px-4 py-3 font-semibold text-foreground-800">Feature</th>
                          <th className="text-left px-4 py-3 font-semibold text-primary-600">Superbuy Spreadsheet</th>
                          <th className="text-left px-4 py-3 font-semibold text-foreground-500">Manual Browsing</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200">
                        {content.comparisonTable.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? '' : 'bg-background-50'}>
                            <td className="px-4 py-3 font-medium text-foreground-700">{row.feature}</td>
                            <td className="px-4 py-3 text-accent-700 font-medium">{row.spreadsheet}</td>
                            <td className="px-4 py-3 text-foreground-500">{row.manual}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <section id="faq" className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {content.faqs.map((faq, i) => (
                    <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/70 transition-colors">
                        <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{faq.question}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.answer}</div>
                    </details>
                  ))}
                </div>
              </section>

              {related.length > 0 && (
                <section className="mb-12 pt-8 border-t border-background-200">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">Related Articles</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {related.map((ra) => (
                      <Link
                        key={ra.id}
                        to={ra.slug}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                        className="group block rounded-xl bg-background-100 overflow-hidden hover:bg-background-200/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-[16/10] w-full overflow-hidden">
                          <img
                            src={getRelatedImage(ra.slug)}
                            alt={ra.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <span className="text-xs text-accent-600 font-medium">{ra.category}</span>
                          <h3 className="text-sm font-semibold text-foreground-800 mt-1 line-clamp-2 leading-snug">{ra.title}</h3>
                          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                            <span>{ra.readingTime} min read</span>
                            <span>{ra.publishDate}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              <section className="mb-12 bg-secondary-50 rounded-2xl p-6 md:p-8">
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                  <div className="w-16 h-16 rounded-full bg-accent-200 overflow-hidden shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20portrait%20warm%20neutral%20studio%20lighting%20friendly%20headshot%20clean%20background%20modern%20editorial&width=160&height=160&seq=article-authbox-dyn-v3-sb&orientation=squarish"
                      alt="Superbuy Research Team"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-1">Reviewed By</p>
                    <p className="text-lg font-heading font-bold text-foreground-900">Superbuy Research Team</p>
                    <p className="text-sm text-foreground-500 mt-1">5+ years in international shopping guides</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {['Superbuy', 'Spreadsheet Research', 'Shipping Guides', 'Product Sourcing'].map((tag) => (
                        <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-200 text-secondary-800 font-medium">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <div className="text-center py-8 mb-8 bg-primary-50 rounded-2xl">
                <p className="font-heading text-xl font-bold text-foreground-900 mb-3">Ready to Start Shopping?</p>
                <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">Access the most comprehensive Superbuy spreadsheet collection with 14 categories updated daily.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                    Superbuy Spreadsheet <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-semibold hover:bg-foreground-100 transition-all whitespace-nowrap cursor-pointer">
                    Back to Home <i className="ri-home-4-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(authorSchema) }} />
    </>
  );
}